import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Users, Plus, Search, Filter, Download, Edit2, Trash2, X, Check } from "lucide-react";

const CATEGORIES = ["PEÓN", "CONDUCTOR", "P. CONDUCTOR", "INSPECTOR", "OFICIAL", "ENCARGADO"];

export default function Personnel() {
  const { authAxios } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [filterContract, setFilterContract] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPerson, setEditPerson] = useState(null);
  const [form, setForm] = useState({ name: "", surname: "", full_name: "", category: "PEÓN", role: "", contract_id: "", work_center: "", phone: "", email: "", active: true, schedule_days: ["L","M","X","J","V"], hire_date: "" });

  useEffect(() => {
    Promise.all([
      authAxios.get("/personnel"),
      authAxios.get("/contracts")
    ]).then(([p, c]) => {
      setPersonnel(p.data);
      setContracts(c.data);
      if (c.data.length > 0) setForm(f => ({ ...f, contract_id: c.data[0].id }));
    }).finally(() => setLoading(false));
  }, []);

  const filtered = personnel.filter(p => {
    const matchesContract = !filterContract || p.contract_id === filterContract;
    const matchesCategory = !filterCategory || p.category === filterCategory;
    const matchesSearch = !search || p.full_name?.toLowerCase().includes(search.toLowerCase());
    return matchesContract && matchesCategory && matchesSearch;
  });

  const contractName = (id) => contracts.find(c => c.id === id)?.name || id;
  const contractColor = (id) => contracts.find(c => c.id === id)?.color || "#007934";

  const DEFAULT_FORM = {
    name: "", surname: "", full_name: "", category: "PEÓN", role: "",
    contract_id: "", work_center: "", phone: "", email: "",
    active: true, schedule_days: ["L", "M", "X", "J", "V"], hire_date: ""
  };

  const openNew = () => {
    setEditPerson(null);
    setForm({ ...DEFAULT_FORM, contract_id: contracts[0]?.id || "" });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditPerson(p);
    // Merge defaults with the personnel record so SAP HR-synced staff
    // (which lack schedule_days/role/phone/etc.) don't crash the form.
    setForm({
      ...DEFAULT_FORM,
      ...p,
      schedule_days: Array.isArray(p.schedule_days) && p.schedule_days.length > 0
        ? p.schedule_days
        : DEFAULT_FORM.schedule_days,
      category: p.category || DEFAULT_FORM.category,
      name: p.name || "",
      surname: p.surname || "",
      work_center: p.work_center || "",
      phone: p.phone || "",
      email: p.email || "",
      role: p.role || "",
      hire_date: p.hire_date || "",
      active: p.active !== false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const fullName = form.full_name || `${form.surname}, ${form.name}`.trim();
    // Whitelist only the fields the backend PersonnelCreate model expects.
    const payload = {
      name: form.name || "",
      surname: form.surname || "",
      full_name: fullName,
      category: form.category || "",
      role: form.role || "",
      contract_id: form.contract_id,
      work_center: form.work_center || "",
      phone: form.phone || null,
      email: form.email || null,
      active: form.active !== false,
      schedule_days: form.schedule_days || ["L", "M", "X", "J", "V"],
      hire_date: form.hire_date || null,
    };
    try {
      if (editPerson) {
        const r = await authAxios.put(`/personnel/${editPerson.id}`, payload);
        setPersonnel(prev => prev.map(p => p.id === editPerson.id ? r.data : p));
      } else {
        const r = await authAxios.post("/personnel", payload);
        setPersonnel(prev => [...prev, r.data]);
      }
      setShowModal(false);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.response?.data?.detail || "No se pudo guardar el operario");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este operario?")) return;
    await authAxios.delete(`/personnel/${id}`);
    setPersonnel(prev => prev.filter(p => p.id !== id));
  };

  const SCHEDULE_DAYS = ["L", "M", "X", "J", "V", "S", "D"];
  const toggleDay = (day) => {
    setForm(f => {
      const current = Array.isArray(f.schedule_days) ? f.schedule_days : ["L", "M", "X", "J", "V"];
      return {
        ...f,
        schedule_days: current.includes(day) ? current.filter(d => d !== day) : [...current, day]
      };
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-10 h-10 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Personal</h1>
          <p className="text-slate-400 text-sm mt-0.5">{personnel.filter(p => p.active).length} operarios activos en {contracts.length} contratos</p>
        </div>
        <button
          data-testid="add-personnel-btn"
          onClick={openNew}
          className="flex items-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md transition-all active:scale-95"
        >
          <Plus size={16} /> Añadir Operario
        </button>
      </div>

      {/* Stats by contract */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in-up">
        {contracts.map(c => {
          const count = personnel.filter(p => p.contract_id === c.id && p.active).length;
          return (
            <div key={c.id} className="card-white p-4 border-l-4" style={{ borderLeftColor: c.color || "#007934" }}>
              <p className="text-xs font-semibold text-slate-400 truncate">{c.name}</p>
              <p className="text-2xl font-black text-slate-800 mt-1">{count}</p>
              <p className="text-xs text-slate-400">operarios activos</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 animate-fade-in-up">
        <div className="relative flex-1 min-w-40">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="search-personnel"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007934]/20 focus:border-[#007934]"
          />
        </div>
        <select
          data-testid="filter-contract"
          value={filterContract}
          onChange={e => setFilterContract(e.target.value)}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20 focus:border-[#007934]"
        >
          <option value="">Todos los contratos</option>
          {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          data-testid="filter-category"
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20 focus:border-[#007934]"
        >
          <option value="">Todas las categorías</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <span className="text-xs text-slate-400 self-center">{filtered.length} registros</span>
      </div>

      {/* Table */}
      <div className="card-white overflow-hidden animate-fade-in-up">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Categoría</th>
              <th>Contrato</th>
              <th>Centro</th>
              <th>Días</th>
              <th>Teléfono</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} data-testid={`personnel-${p.id}`}>
                <td>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: contractColor(p.contract_id) }}>
                      {p.full_name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{p.full_name}</p>
                      {p.email && <p className="text-xs text-slate-400">{p.email}</p>}
                    </div>
                  </div>
                </td>
                <td><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">{p.category}</span></td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: contractColor(p.contract_id) }} />
                    <span className="text-sm text-slate-600">{contractName(p.contract_id)}</span>
                  </div>
                </td>
                <td className="text-sm text-slate-500">{p.work_center}</td>
                <td className="text-xs font-mono text-slate-400">{(p.schedule_days || []).join("-")}</td>
                <td className="text-sm text-slate-500">{p.phone || "—"}</td>
                <td>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.active ? "badge-ok" : "badge-draft"}`}>
                    {p.active ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-[#007934] transition-colors"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Users size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No se encontraron operarios</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900">{editPerson ? "Editar Operario" : "Nuevo Operario"}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Apellidos</label>
                <input value={form.surname || ""} onChange={e => setForm(f => ({ ...f, surname: e.target.value }))} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20" placeholder="GARCIA FERNANDEZ" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Nombre</label>
                <input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20" placeholder="MANUEL" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Categoría</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contrato</label>
                <select value={form.contract_id} onChange={e => setForm(f => ({ ...f, contract_id: e.target.value }))} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20">
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Centro de trabajo</label>
                <input value={form.work_center || ""} onChange={e => setForm(f => ({ ...f, work_center: e.target.value }))} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20" placeholder="Zona Norte" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Teléfono</label>
                <input value={form.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20" placeholder="600 000 000" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Días de trabajo</label>
                <div className="flex gap-2">
                  {SCHEDULE_DAYS.map(d => (
                    <button key={d} type="button" onClick={() => toggleDay(d)}
                      className={`w-9 h-9 rounded-lg text-xs font-bold transition-all ${(form.schedule_days || []).includes(d) ? "bg-[#007934] text-white" : "bg-slate-100 text-slate-400"}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="active" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
                <label htmlFor="active" className="text-sm text-slate-600">Operario activo</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancelar</button>
              <button data-testid="save-personnel-btn" onClick={handleSave} className="px-5 py-2 text-sm font-semibold bg-[#007934] text-white rounded-lg hover:bg-[#005c27] shadow-md transition-all">
                {editPerson ? "Actualizar" : "Crear"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
